import React, { useState } from "react";
import {
  Form,
  CardHeader,
  CardContent,
  Card,
  Icon,
  FormGroup,
  Button,
  Input,
  Popup,
  Confirm,
} from "semantic-ui-react";
import Swal from "sweetalert2";
import { arrayMove } from "@dnd-kit/sortable";
import { useFormik } from "formik";
import { BasicModal } from "../../../../../Shared";
import { NewWeekForm } from "./NewWeekForm";
import { Session } from "../../../../../../api";
import { useAuth } from "../../../../../../hooks";
import { initialValues, validationSchema } from "./SessionForm.form";
import "./SessionForm.scss";

const sessionController = new Session();

const Toast = Swal.mixin({
  toast: true,
  position: "bottom-end",
  showConfirmButton: false,
  timer: 2000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.onmouseenter = Swal.stopTimer;
    toast.onmouseleave = Swal.resumeTimer;
  },
});

export function SessionForm(props) {
  const {
    weeks,
    weeksOptions,
    setWeeksAndSessions,
    session,
    onReloadWeek,
    setActiveSessionEdit,
    onReload,
  } = props;
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { accessToken } = useAuth();

  const onOpenCloseConfirm = () => setShowConfirm((prevState) => !prevState);
  const onOpenCloseModal = () => setShowModal((prevState) => !prevState);

  /**
   * Initializes and manages the form for creating or updating a session using the useFormik hook.
   *
   * The form supports both the creation of new sessions and the updating of a session. It handles session details such as title, week association, type, and state (active or inactive).
   *
   * Key Features:
   * - **Form Initialization**: Sets initial form values based on the provided session object or defaults for a new session.
   * - **Validation**: Utilizes a custom validation schema to ensure all form fields meet specific criteria before submission.
   * - **Session Management**: Allows for the updating of session details or the creation of a new session, including adjusting the session's week association and reordering sessions within weeks.
   * - **UI Feedback**: Provides success notifications upon successful creation or update of a session.
   * - **Error Handling**: Catches and logs any errors that occur during the form submission process.
   *
   * Form Submission Process:
   * 1. Trims the session title to remove any leading or trailing whitespace.
   * 2. Finds the currently selected week and the session's original week (if updating).
   * 3. Updates the session's details in the local state.
   * 4. Constructs the data object with the session's title, week ID, type, and state.
   * 5. If updating an existing session:
   *    - Calls `updateSession` to save changes to the backend.
   *    - If the week association has changed, updates the session order in both the original and new weeks.
   * 6. If creating a new session:
   *    - Calls `createSession` with the new session details.
   *    - Resets the form fields for another entry.
   * 7. Triggers a UI refresh to reflect the changes.
   */
  const formik = useFormik({
    initialValues: initialValues(session),
    validationSchema: validationSchema(),
    validateOnChange: false,
    enableReinitialize: true,
    onSubmit: async (formValue) => {
      try {
        let weekSelected = weeks.find((week) => week.id === formValue.weekId);
        let beforeWeek = weeks.find((week) => week.id === session?.weekId);
        formik.values.title = formik.values.title.trim();
        session.title = formValue.title;

        if (beforeWeek !== undefined) {
          let sessionBeforeWeek = beforeWeek.sessions.find(
            (s) => s.id === session.id
          );
          sessionBeforeWeek.title = formValue.title;
          sessionBeforeWeek.state = formValue.state === "ACTIVO";
          sessionBeforeWeek.type = formValue.type;
        }
        setWeeksAndSessions(weeks);

        const data = {
          title: formValue.title,
          weekId: formValue.weekId,
          type: formValue.type,
          state: formValue.state === "ACTIVO",
        };
        if (session.id !== undefined) {
          await sessionController.updateSession(accessToken, session.id, data);
          if (session.weekId !== data.weekId) {
            session.weekId = data.weekId;
            //* Ordenar sesiones de unidad anterior
            beforeWeek.sessions = beforeWeek.sessions.filter(
              (s) => s.id !== session.id
            );
            let newOrderSessionsBeforeWeek = arrayMove(
              beforeWeek.sessions,
              beforeWeek.sessions.length,
              beforeWeek.sessions.length
            );
            newOrderSessionsBeforeWeek = newOrderSessionsBeforeWeek.filter(
              (element) => element !== undefined
            );
            //* Ordenar sesiones de unidad nueva
            weekSelected.sessions.push(session);
            let newOrderSessionsNewWeek = arrayMove(
              weekSelected.sessions,
              weekSelected.sessions.length,
              weekSelected.sessions.length
            );
            newOrderSessionsNewWeek = newOrderSessionsNewWeek.filter(
              (element) => element !== undefined
            );
            try {
              let data = {
                sessions: newOrderSessionsNewWeek,
              };
              await sessionController.updateIndexWeekSessions(
                accessToken,
                data
              );
              data = {
                sessions: newOrderSessionsBeforeWeek,
              };
              await sessionController.updateIndexWeekSessions(
                accessToken,
                data
              );
            } catch (error) {
              console.error(error);
            }
          }
          Toast.fire({
            icon: "success",
            title: `Se modificó la sesión ${data.title} con éxito.`,
          });
        } else {
          data.index = weekSelected.sessions.length;
          await sessionController.createSession(accessToken, data);
          formik.values.title = "";
          formik.values.weekId = "";
          formik.values.type = "";
          formik.values.state = "";
          setActiveSessionEdit({});
          Toast.fire({
            icon: "success",
            title: "Se ha creado la sesión con éxito.",
          });
        }
        onReload();
      } catch (error) {
        console.error(error);
      }
    },
  });

  /**
   * Asynchronously deletes a session and provides user feedback.
   *
   * This function, `handleDeleteSession`, is designed to delete a specific session when invoked. It performs the deletion by calling `sessionController.deleteSession` with the necessary parameters, including an access token and the session's unique identifier (`_id`).
   *
   * Steps:
   * 1. **Deletion Request**: Initiates an asynchronous request to delete the session using the session controller's `deleteSession` method.
   * 2. **Success Feedback**: Upon successful deletion, displays a success message using `Toast.fire` to inform the user that the session has been successfully removed. The message includes the title of the deleted session for clarity.
   * 3. **State Update**: Resets the active session being edited to an empty object, indicating that there is no longer an active session selected for editing.
   * 4. **UI Refresh**: Calls `onReload` to refresh the list of sessions, ensuring the UI reflects the current state without the deleted session.
   * 5. **Close Confirmation Dialog**: Invokes `onOpenCloseConfirm` to close the confirmation dialog, assuming it was open as part of a confirmation process for deleting the session.
   *
   * Error Handling:
   * - In case of an error during the deletion process, logs the error to the console for debugging purposes.
   */
  const handleDeleteSession = async () => {
    try {
      await sessionController.deleteSession(accessToken, session.id);
      Toast.fire({
        icon: "success",
        title: `Se eliminó la sesión ${session.title} con éxito.`,
      });
      setActiveSessionEdit({});
      onReload();
      onOpenCloseConfirm();
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Transforms an array of option values into an array of objects suitable for dropdown menus.
   *
   * This function takes an array of strings (`options`) and maps each string to an object with `value` and `text` properties,
   * where both properties are set to the value of the string. This transformation is necessary for integrating with UI components
   * that require options to be provided in an object format, where the `value` is used as the identifier for the option, and the
   * `text` is what is displayed to the user in the dropdown menu.
   *
   * Example:
   * If `options` is `['Option1', 'Option2']`, the output will be `[{ value: 'Option1', text: 'Option1' }, { value: 'Option2', text: 'Option2' }]`.
   *
   * @param {string[]} options - An array of strings representing the option values.
   * @returns {Object[]} An array of objects with `value` and `text` properties.
   */
  const transformOptions = (options) =>
    options.map((option) => ({
      value: option,
      text: option,
    }));

  const typeSessionOptions = ["CLASE", "CUESTIONARIO", "EXAMEN"];
  const stateOptions = ["ACTIVO", "INACTIVO"];
  const typeSessionTransformed = transformOptions(typeSessionOptions);
  const stateTransformed = transformOptions(stateOptions);

  return (
    <div className="session-div" style={{ padding: "0 1rem" }}>
      <Card className="session-form-card" fluid>
        <CardContent className="session-form-card__header">
          <CardHeader>
            <Icon name="clipboard outline" />
            Datos de sesión
          </CardHeader>
        </CardContent>
        <CardContent className="session-form">
          <Form onSubmit={formik.handleSubmit}>
            <FormGroup>
              <Form.Input
                name="title"
                placeholder="Título"
                onChange={formik.handleChange}
                value={formik.values.title.trimStart()}
                error={formik.errors.title}
              />
              <Input action className="select-group field">
                <Form.Select
                  className="edunova-select"
                  name="weekId"
                  placeholder="Selecciona unidad"
                  clearable
                  options={weeksOptions}
                  onChange={(e, { value }) =>
                    formik.setFieldValue("weekId", value)
                  }
                  value={formik.values.weekId}
                  error={formik.errors.weekId}
                />
                <Popup
                  content="Crear unidad"
                  trigger={
                    <Button type="button" icon onClick={onOpenCloseModal}>
                      <Icon name="plus" />
                    </Button>
                  }
                />
              </Input>
              <Form.Select
                className="edunova-select"
                name="type"
                placeholder="Tipo de sesión"
                clearable
                options={typeSessionTransformed}
                onChange={(e, { value }) => formik.setFieldValue("type", value)}
                value={formik.values.type}
                error={formik.errors.type}
              />
              <Form.Select
                className="edunova-select"
                name="state"
                placeholder="Estado"
                clearable
                options={stateTransformed}
                onChange={(e, { value }) =>
                  formik.setFieldValue("state", value)
                }
                value={formik.values.state}
                error={formik.errors.state}
              />
              <Popup
                content="Guardar datos de sesión"
                trigger={
                  <Form.Button
                    className="save-button"
                    fluid
                    primary
                    type="submit"
                    icon="save"
                    loading={formik.isSubmitting}
                  />
                }
              />
              <Popup
                content="Eliminar sesión"
                trigger={
                  <Form.Button
                    className="delete-button"
                    fluid
                    type="button"
                    color="red"
                    icon="trash alternate"
                    disabled={session.id !== undefined ? false : true}
                    onClick={onOpenCloseConfirm}
                  />
                }
              />
            </FormGroup>
          </Form>
        </CardContent>
      </Card>
      <BasicModal
        show={showModal}
        close={onOpenCloseModal}
        title="Crear unidad"
      >
        <NewWeekForm
          cantWeeks={weeksOptions.length}
          onClose={onOpenCloseModal}
          onReloadWeek={onReloadWeek}
        />
      </BasicModal>
      <Confirm
        confirmButton="Aceptar"
        cancelButton="Cancelar"
        open={showConfirm}
        onCancel={onOpenCloseConfirm}
        onConfirm={handleDeleteSession}
        content={`¿Estás seguro de eliminar la sesión ${session?.title}?`}
        size="mini"
      />
    </div>
  );
}
